import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { dataDogLogger } from 'platform/monitoring/Datadog/utilities';

import FormApp from 'platform/forms-system/src/js/containers/FormApp';
import {
  getNextPagePath,
  checkValidPagePath,
} from 'platform/forms-system/src/js/routing';

import {
  Element,
  scrollToTop,
  getScrollOptions,
} from 'platform/utilities/scroll';
import environment from 'platform/utilities/environment';
import { restartShouldRedirect } from 'platform/site-wide/wizard';
import {
  LOAD_STATUSES,
  PREFILL_STATUSES,
  SAVE_STATUSES,
  setFetchFormStatus,
  fetchInProgressForm,
} from './actions';

import { isInProgressPath } from '../helpers';
import { getSaveInProgressState } from './selectors';
import { APP_TYPE_DEFAULT } from '../../forms-system/src/js/constants';

const getAdditionalNonFormPaths = (routes = []) =>
  routes
    .filter(route => route.allowDirectAccess !== false)
    .map(route => route.path);

// Identity verification flips the default: a route is protected unless it opts
// in, rather than open unless it opts out.
const getOptInDirectAccessPaths = (routes = []) =>
  routes
    .filter(route => route.allowDirectAccess === true)
    .map(route => route.path);

/*
 * Primary component for a schema generated form app.
 */
class RoutedSavableApp extends React.Component {
  constructor(props) {
    super(props);
    this.FormApp = props.FormApp || FormApp;
    this.location = props.location || window.location;
    this.shouldRedirectOrLoad = false;
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillMount() {
    window.addEventListener('beforeunload', this.onbeforeunload);
    document.body.addEventListener('click', this.checkExitFormLink, true);

    if (window.History) {
      window.History.scrollRestoration = 'manual';
    }

    // If we start in the middle of a form, redirect to the beginning or load
    //  saved form / prefill
    // If we're in production, we'll redirect if we start in the middle of a form
    // In development, we won't redirect unless we append the URL with `?redirect`
    const { currentLocation, formConfig } = this.props;
    const additionalNonFormPaths = getAdditionalNonFormPaths(
      formConfig.additionalRoutes,
    );
    const trimmedPathname = currentLocation.pathname.replace(/\/$/, '');
    const resumeForm = trimmedPathname.endsWith('resume');
    const devRedirect =
      (!environment.isLocalhost() &&
        !currentLocation.search.includes('skip')) ||
      currentLocation.search.includes('redirect');
    const goToStartPage = resumeForm || devRedirect;
    if (
      isInProgressPath(currentLocation.pathname, additionalNonFormPaths) &&
      goToStartPage
    ) {
      // We started on a page that isn't the first, so after we know whether
      //  we're logged in or not, we'll load or redirect as needed.
      this.shouldRedirectOrLoad = true;
    }
  }

  componentDidMount() {
    // When a user isn't logged in, the profile finishes loading before the
    // component mounts.
    if (this.props.profileIsLoading) return;
    if (this.enforceIdentityVerification(this.props)) return;
    if (this.shouldRedirectOrLoad) this.redirectOrLoad(this.props);
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillReceiveProps(newProps) {
    // When a user is logged in, the profile finishes loading after the component
    // has mounted, so enforce identity verification before loading or redirecting
    // into the form.
    if (
      this.props.profileIsLoading &&
      !newProps.profileIsLoading &&
      this.shouldRedirectOrLoad
    ) {
      if (this.enforceIdentityVerification(newProps)) return;
      this.redirectOrLoad(newProps);
    }

    const status = newProps.loadedStatus;
    if (status === LOAD_STATUSES.success) {
      // Redirect-time normalization: allow form to rewrite returnUrl (e.g. array-builder item page → summary) so resume lands on a stable page
      const returnUrlToUse =
        typeof newProps.formConfig?.normalizeReturnUrl === 'function'
          ? newProps.formConfig.normalizeReturnUrl(newProps.returnUrl)
          : newProps.returnUrl;
      const safeReturnUrl = this.getSafeReturnUrl(newProps, returnUrlToUse);
      const propsWithNormalizedReturnUrl = {
        ...newProps,
        returnUrl: safeReturnUrl,
      };

      if (
        newProps.currentLocation &&
        newProps.currentLocation.pathname.endsWith('resume')
      ) {
        newProps.router.replace(safeReturnUrl);
      } else if (newProps.formConfig.onFormLoaded) {
        // The onFormLoaded callback should handle navigating to the start of the form
        newProps.formConfig.onFormLoaded(propsWithNormalizedReturnUrl);
      } else {
        newProps.router.push(safeReturnUrl);
      }
      // Set loadedStatus in redux to not-attempted to not show the loading page
      newProps.setFetchFormStatus(LOAD_STATUSES.notAttempted);
    } else if (
      newProps.prefillStatus !== this.props.prefillStatus &&
      newProps.prefillStatus === PREFILL_STATUSES.unfilled
    ) {
      let newRoute;
      const { formConfig = {} } = newProps;
      const { saveInProgress = {} } = formConfig;
      if (
        newProps.isStartingOver &&
        typeof saveInProgress.restartFormCallback === 'function' &&
        restartShouldRedirect(formConfig.wizardStorageKey)
      ) {
        // Restart callback returns a new route
        newRoute = saveInProgress?.restartFormCallback();
      }

      // Form restart redirects to new route or the first page after the intro
      newProps.router.push(newRoute || this.getFirstNonIntroPagePath(newProps));
    } else if (
      status !== LOAD_STATUSES.notAttempted &&
      status !== LOAD_STATUSES.pending &&
      status !== this.props.loadedStatus &&
      !this.location.pathname.endsWith('/error')
    ) {
      let action = 'push';
      if (this.location.pathname.endsWith('resume')) {
        action = 'replace';
      }
      newProps.router[action](`${newProps.formConfig.urlPrefix || ''}error`);
    }
  }

  componentDidUpdate(oldProps) {
    // Prevent unverified users from accessing protected form pages after updates
    if (this.enforceIdentityVerification(this.props)) return;

    // Scroll up to top while user is waiting for form to load or save
    if (
      (oldProps.loadedStatus !== this.props.loadedStatus &&
        this.props.loadedStatus === LOAD_STATUSES.pending) ||
      (oldProps.savedStatus !== this.props.savedStatus &&
        this.props.savedStatus === SAVE_STATUSES.pending)
    ) {
      scrollToTop('topScrollElement', getScrollOptions());
    }

    if (
      this.props.savedStatus !== oldProps.savedStatus &&
      this.props.savedStatus === SAVE_STATUSES.success
    ) {
      this.props.router.push(
        `${this.props.formConfig.urlPrefix || ''}form-saved`,
      );
    }
  }

  // I’m not convinced this is ever executed
  componentWillUnmount() {
    this.removeOnbeforeunload();
  }

  onbeforeunload = e => {
    const { currentLocation = {}, autoSavedStatus, formConfig } = this.props;

    const isCypressRunningInCI =
      typeof Cypress !== 'undefined' && Cypress.env('CI');

    // Disable browser window unload alert. This may prevent 40 minute timeout
    // errors in CI
    if (
      formConfig.dev?.disableWindowUnloadInCI &&
      (isCypressRunningInCI ||
        currentLocation.href?.startsWith('http://localhost'))
    ) {
      return null;
    }

    const appType = formConfig?.customText?.appType || APP_TYPE_DEFAULT;
    const trimmedPathname = currentLocation.pathname.replace(/\/$/, '');
    const additionalNonFormPaths = getAdditionalNonFormPaths(
      formConfig.additionalRoutes,
    );
    let message;
    if (
      autoSavedStatus !== SAVE_STATUSES.success &&
      isInProgressPath(trimmedPathname, additionalNonFormPaths)
    ) {
      message = `Are you sure you wish to leave this ${appType}? All progress will be lost.`;
      // Chrome requires this to be set
      e.returnValue = message;
    }
    return message;
  };

  // eslint-disable-next-line class-methods-use-this
  getIntroPagePath(props) {
    return props.routes[props.routes.length - 1].pageList[0].path;
  }

  // eslint-disable-next-line class-methods-use-this
  getFirstNonIntroPagePath(props) {
    return getNextPagePath(
      props.routes[props.routes.length - 1].pageList,
      props.formData,
      `${props.formConfig?.urlPrefix || '/'}introduction`,
    );
  }

  // Keep resume navigation on active form pages or explicitly allowed
  // additional non-form routes.
  getSafeReturnUrl(props, returnUrl) {
    const additionalNonFormPaths = getAdditionalNonFormPaths(
      props.formConfig.additionalRoutes,
    );
    const isValidReturnUrl = checkValidPagePath(
      props.routes[props.routes.length - 1].pageList,
      props.formData,
      returnUrl,
    );
    const trimmedReturnUrl = returnUrl?.split(/[?#]/)[0].replace(/\/$/, '');
    const isDirectAccessNonFormPath = additionalNonFormPaths.some(path =>
      trimmedReturnUrl?.endsWith(path),
    );

    if (!isValidReturnUrl && !isDirectAccessNonFormPath) {
      const inProgressFormId = props.savedForms?.find(
        savedForm => savedForm.form === props.formConfig?.formId,
      )?.metadata?.inProgressFormId;
      const fallbackPath = this.getFirstNonIntroPagePath(props);
      dataDogLogger({
        message: 'Invalid returnUrl on form resume',
        attributes: {
          returnUrl: returnUrl || '(empty)',
          fallbackPath,
          formId: props.formConfig?.formId,
          hasInProgressFormId: !!inProgressFormId,
          ...(inProgressFormId ? { inProgressFormId } : {}),
        },
        status: 'warn',
      });
      return fallbackPath;
    }

    return returnUrl;
  }

  removeOnbeforeunload = () => {
    window.removeEventListener('beforeunload', this.onbeforeunload);
    document.body.removeEventListener('click', this.checkExitFormLink);
  };

  // Allow va-link, va-button, or button (progress buttons) to leave form flow
  // without showing browser window unload alert
  checkExitFormLink = ({ target }) => {
    if (
      ['VA-LINK', 'VA-BUTTON', 'BUTTON'].includes(target.tagName) &&
      target.classList.contains('exit-form')
    ) {
      this.removeOnbeforeunload();
    }
  };

  enforceIdentityVerification(props) {
    const {
      formConfig,
      currentLocation,
      profileIsLoading,
      isLoggedIn,
      isUserVerified,
    } = props;

    const enforceVerification =
      formConfig.requireIdentityVerification &&
      !profileIsLoading &&
      isLoggedIn &&
      !isUserVerified;

    if (!enforceVerification) return false;

    // Signed-out users never reach here, so a route that collects identity
    // before sign-in stays reachable.
    const isProtectedFormPath = isInProgressPath(
      currentLocation.pathname,
      getOptInDirectAccessPaths(formConfig.additionalRoutes),
    );

    if (isProtectedFormPath) {
      this.shouldRedirectOrLoad = false;
      props.router.replace(this.getIntroPagePath(props));
      return true;
    }

    return false;
  }

  redirectOrLoad(props) {
    // Stop a user that's been redirected from being redirected again after
    // logging in
    this.shouldRedirectOrLoad = false;

    const firstPagePath = this.getIntroPagePath(props);
    const firstNonIntroPagePath = this.getFirstNonIntroPagePath(props);

    // If we're logged in and have a saved / pre-filled form, load that
    if (props.isLoggedIn) {
      const currentForm = props.formConfig.formId;
      const isSaved = props.savedForms.some(
        savedForm => savedForm.form === currentForm,
      );
      const hasPrefillData = props.prefillsAvailable.includes(currentForm);

      if (isSaved) {
        props.fetchInProgressForm(
          currentForm,
          props.formConfig.migrations,
          false,
          props.formConfig.prefillTransformer,
        );
      } else if (props.skipPrefill) {
        // Just need to go to the page after the introduction
        props.router.replace(firstNonIntroPagePath);
      } else if (hasPrefillData) {
        props.fetchInProgressForm(
          currentForm,
          props.formConfig.migrations,
          true,
          props.formConfig.prefillTransformer,
        );
      } else {
        // No forms to load; go to the beginning
        // If the first page is not the intro and uses `depends`, this will probably break
        props.router.replace(firstPagePath);
      }
    } else {
      // Can't load a form; go to the beginning
      // If the first page is not the intro and uses `depends`, this will probably break
      props.router.replace(firstPagePath);
    }
  }

  render() {
    const { currentLocation, formConfig, children, loadedStatus } = this.props;
    const appType = formConfig?.customText?.appType || APP_TYPE_DEFAULT;
    const trimmedPathname = currentLocation.pathname.replace(/\/$/, '');
    let content;
    const loadingForm =
      trimmedPathname.endsWith('resume') ||
      loadedStatus === LOAD_STATUSES.pending;
    if (
      (!formConfig.disableSave &&
        loadingForm &&
        this.props.prefillStatus === PREFILL_STATUSES.pending) ||
      (!formConfig.disableSave && this.shouldRedirectOrLoad)
    ) {
      content = (
        <va-loading-indicator
          label="Loading"
          message="Retrieving your profile information..."
        />
      );
    } else if (!formConfig.disableSave && loadingForm) {
      content = (
        <va-loading-indicator
          label="Loading"
          message={`Retrieving your saved ${appType}...`}
        />
      );
    } else if (
      !formConfig.disableSave &&
      this.props.savedStatus === SAVE_STATUSES.pending
    ) {
      content = (
        <va-loading-indicator
          label="Loading"
          message={`Saving your ${appType}...`}
        />
      );
    } else {
      content = (
        <this.FormApp formConfig={formConfig} currentLocation={currentLocation}>
          {children}
        </this.FormApp>
      );
    }

    return (
      <div>
        <Element name="topScrollElement" />
        {content}
      </div>
    );
  }
}

const mapDispatchToProps = {
  setFetchFormStatus,
  fetchInProgressForm,
};

const ConnectedRoutedSavableApp = connect(
  getSaveInProgressState,
  mapDispatchToProps,
)(RoutedSavableApp);

export default withRouter(ConnectedRoutedSavableApp);

RoutedSavableApp.propTypes = {
  FormApp: PropTypes.any,
  autoSavedStatus: PropTypes.string,
  children: PropTypes.any,
  currentLocation: PropTypes.shape({
    href: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  formConfig: PropTypes.shape({
    additionalRoutes: PropTypes.array,
    customText: PropTypes.shape({
      appType: PropTypes.string,
    }),
    dev: PropTypes.shape({
      disableWindowUnloadInCI: PropTypes.bool,
    }),
    formOptions: PropTypes.shape({
      ignorePageUnloadAlertOnUrls: PropTypes.arrayOf(PropTypes.string),
    }),
    disableSave: PropTypes.bool,
    normalizeReturnUrl: PropTypes.func,
    requireIdentityVerification: PropTypes.bool,
    urlPrefix: PropTypes.string,
  }),
  isLoggedIn: PropTypes.bool,
  isUserVerified: PropTypes.bool,
  loadedStatus: PropTypes.string,
  location: PropTypes.object,
  prefillStatus: PropTypes.string,
  profileIsLoading: PropTypes.bool,
  router: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }),
  savedStatus: PropTypes.string,
};

export { RoutedSavableApp };
